import cn from 'classnames';
import { useCallback, useMemo } from 'react';

import { Button } from '../../../buttons';
import { SEPARATOR } from '../pagination-config';
import { usePaginationContext } from '../pagination-context';

import type { ReactElement } from 'react';

import type { PaginationNavigationItemProps } from './pagination-navigation-item.types';

const PaginationNavigationItem = (props: PaginationNavigationItemProps): ReactElement => {
  const { value } = props;
  const paginationContext = usePaginationContext();

  if (!paginationContext) {
    throw new Error('PaginationNavigationItem must be used within a PaginationContext.Provider');
  }

  const { onPageChange, page } = paginationContext;
  const isPage = typeof value === 'number';
  const label = isPage ? (value + 1).toLocaleString() : value;
  const disabled = value === SEPARATOR || page === value;
  const selected = page === value;

  const ariaLabel = useMemo(() => {
    if (!isPage) {
      return;
    }

    if (selected) {
      return `Current page, page ${label}`;
    }

    return `Go to page ${label}`;
  }, [isPage, selected, label]);

  const handleOnClick = useCallback((): void => {
    if (typeof value === 'number') {
      onPageChange?.(value);
    }
  }, [value, onPageChange]);

  // Render separator as non-interactive element for better accessibility
  if (value === SEPARATOR) {
    return (
      <span aria-hidden="true" className="rbui-pagination__navigation-separator">
        {SEPARATOR}
      </span>
    );
  }

  return (
    <Button
      aria-current={selected ? true : undefined}
      aria-label={ariaLabel}
      className={cn('rbui-pagination__navigation-item', { 'rbui-pagination__navigation-item--selected': selected })}
      disabled={disabled}
      onClick={handleOnClick}
      type="tertiary"
    >
      {label}
    </Button>
  );
};

PaginationNavigationItem.displayName = 'PaginationNavigationItem';

export { PaginationNavigationItem };
